import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { PE_LOGO_SRC } from '../../constants/brandAssets';
import { savePePapersArchiveDoc } from '../../services/pePapersArchiveService';
import useAppStore from '../../store/appStore';
import PePapersStorageHint, { PE_PAPERS_STORAGE_HINTS } from './PePapersStorageHint';
import { downloadHtmlAsPdf } from '../../utils/pe-papers/htmlDocumentPdfDownload';
import {
    UVV_CONFIRMATION_TEXT,
    UVV_DEFAULT_FEUERLOESCHER,
    UVV_DEFAULT_VERBANDBLOCK,
    UVV_DEFAULT_VERBANDKASTEN,
    UVV_DOC_ID,
    UVV_DOC_SUBTITLE,
    UVV_INSTRUCTOR_SIGNATURE_LABEL,
    UVV_INTERVAL_HINT,
    UVV_MODULES_PAGE1,
    UVV_MODULES_PAGE2,
    UVV_SIGNATURE_ROW_COUNT,
    formatUvvTurnusLine,
    resolveUvvBullet,
    type UvvModule,
    type UvvUnterweisungsTurnus,
} from '../../utils/pe-papers/uvvUnterweisungCatalog';

export interface BgwUvvUnterweisungModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: any;
    onArchived?: () => void;
}

interface FormData {
    salonName: string;
    ownerName: string;
    street: string;
    zip: string;
    city: string;
    instructionDate: string;
    standortVerbandkasten: string;
    standortVerbandblock: string;
    standortFeuerloescher: string;
    unterweisungsTurnus: UvvUnterweisungsTurnus;
}

interface FieldLocks {
    salonName: boolean;
    ownerName: boolean;
    street: boolean;
    zip: boolean;
    city: boolean;
}

const A4_PAGE_CLASS =
    'uvv-a4-page bg-white text-black mx-auto w-full max-w-[210mm] min-h-[297mm] h-auto shadow-xl rounded-sm text-left block overflow-visible box-border my-4 print:my-0 print:shadow-none print:rounded-none';

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6C299]/50 transition-colors';

const LABEL_CLASS = 'text-xs font-semibold text-pe-muted dark:text-slate-400 uppercase tracking-wider mb-1 block';

const READONLY_INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] text-pe-accent-text dark:text-[#E6C299] px-4 py-2.5 text-sm cursor-not-allowed opacity-90';

const ERROR_INPUT_RING = 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500/20';

const STEP_LABELS: Record<number, string> = {
    1: 'Datenerfassung & Überprüfung',
    2: 'Vorschau & Export',
};

/**
 * Lesbare UVV auf Ausdruck — 2 Seiten OK, kein extremes Downscale.
 * Regel: .uvv-section bleibt zusammen (kein Schnitt mitten in Überschrift/Liste).
 */
const UVV_DOC_EXTRA_CSS = `
  .uvv-a4-page {
    font-family: "Segoe UI", Arial, Helvetica, sans-serif !important;
    font-size: 12.5px !important;
    line-height: 1.4 !important;
    color: #0f172a !important;
    background: #fff !important;
    box-sizing: border-box !important;
    padding: 10mm 11mm !important;
  }
  .uvv-screen-only { display: block; }
  .uvv-page-label {
    font-size: 12.5px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
    color: #64748b; margin: 0 0 6px; padding-bottom: 3px; border-bottom: 1px dashed #cbd5e1;
  }
  .uvv-header {
    width: 100%; border-collapse: collapse;
    margin: 0 0 8px; padding: 0 0 8px;
    border-bottom: 2px solid #E6C299;
  }
  .uvv-header td { vertical-align: top; padding: 0; border: none; background: transparent; }
  .uvv-header-logo { width: 48px; padding-right: 10px !important; }
  .uvv-logo { height: 36px; width: auto; max-width: 96px; object-fit: contain; display: block; }
  .uvv-brand-kicker {
    font-size: 11px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase;
    color: #64748b; margin: 0 0 2px;
  }
  .uvv-doc-title {
    font-size: 16px !important; font-weight: 800 !important; line-height: 1.2 !important;
    margin: 0 0 2px !important; text-align: left !important; color: #0f172a !important;
    background: none !important; border: none !important; text-transform: none !important;
    letter-spacing: normal !important; padding: 0 !important;
  }
  .uvv-title-pe { font-style: italic; color: #E6C299 !important; font-weight: 800; }
  .uvv-title-rest { color: #0f172a !important; font-weight: 800; }
  .uvv-title-sub {
    display: block; font-size: 13px !important; font-weight: 700 !important;
    color: #0f172a !important; margin-top: 1px; letter-spacing: normal !important;
    text-transform: none !important;
  }
  .uvv-doc-sub {
    font-size: 12.5px !important; font-weight: 600 !important; margin: 0 !important;
    color: #475569 !important; background: none !important; text-align: left !important;
  }
  .uvv-header-meta {
    width: 168px; text-align: right; font-size: 12.5px; color: #475569; line-height: 1.4;
    white-space: nowrap; padding-left: 12px !important;
  }
  .uvv-header-meta p { margin: 0 0 2px !important; text-align: right !important; font-size: 12.5px !important; }
  .uvv-header-meta strong { color: #0f172a; font-weight: 700; }
  .uvv-lead {
    font-size: 12.5px; color: #64748b; margin: 0 0 7px; line-height: 1.4;
  }
  .uvv-lead strong { color: #334155; }
  .uvv-stammdaten {
    font-size: 12px !important; line-height: 1.4 !important; margin: 0 0 8px !important;
    color: #1e293b !important; border: 1px solid #e2e8f0 !important;
    border-radius: 8px !important; padding: 7px 9px !important;
    background: #f8fafc !important;
  }
  .uvv-stammdaten strong { color: #0f172a; }
  .uvv-module { margin: 0 0 6px !important; }
  .uvv-module h3, .uvv-module .uvv-mod-title, h3.uvv-mod-title {
    font-size: 12.5px !important; font-weight: 800 !important; text-transform: uppercase !important;
    letter-spacing: 0.04em !important;
    border: none !important; border-bottom: 1.5px solid #E6C299 !important;
    padding: 0 0 3px !important; margin: 0 0 5px !important;
    background: transparent !important; color: #0f172a !important;
    line-height: 1.25 !important; text-align: left !important;
  }
  .uvv-section {
    margin: 0 0 4px !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  .uvv-module h4, .uvv-module .uvv-sec-title, h4.uvv-sec-title {
    display: flex !important;
    align-items: flex-start !important;
    gap: 7px !important;
    font-size: 12px !important; font-weight: 700 !important; margin: 2px 0 2px !important;
    padding: 0 !important; background: transparent !important; color: #0f172a !important;
    border: none !important; text-transform: none !important; text-align: left !important;
    line-height: 1.3 !important;
  }
  .uvv-tickbox {
    display: inline-block !important;
    flex: 0 0 13px !important;
    width: 13px !important;
    height: 13px !important;
    min-width: 13px !important;
    min-height: 13px !important;
    border: 1.8px solid #0f172a !important;
    border-radius: 2px !important;
    margin: 1px 0 0 0 !important;
    padding: 0 !important;
    vertical-align: top !important;
    background: #fff !important;
    box-sizing: border-box !important;
  }
  .uvv-tick-label {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    display: block !important;
  }
  .uvv-module ul { margin: 0 0 1px !important; padding-left: 20px !important; list-style: disc !important; }
  .uvv-module li {
    font-size: 12px !important; line-height: 1.4 !important; margin: 0 0 2.5px !important;
    text-align: left; color: #1e293b !important;
    orphans: 2; widows: 2;
    list-style: disc !important;
  }
  .uvv-module li::before { content: none !important; display: none !important; }
  .uvv-confirm {
    font-size: 12px !important; line-height: 1.4 !important; color: #334155 !important;
    margin: 0 0 5px !important; text-align: justify;
  }
  .uvv-sig-wrap {
    overflow: hidden; border-radius: 8px; border: 1px solid #e2e8f0;
    background: #fff; margin: 4px 0 6px;
  }
  .uvv-sig-table { width: 100%; border-collapse: collapse; margin: 0; font-size: 12.5px; }
  .uvv-sig-table th, .uvv-sig-table td {
    border: none; border-bottom: 1.5px solid #475569;
    padding: 5px 7px; text-align: left; vertical-align: middle;
  }
  .uvv-sig-table th {
    background: #f1f5f9; font-weight: 800; font-size: 12.5px;
    text-transform: uppercase; letter-spacing: 0.04em; color: #475569;
  }
  .uvv-sig-table td { background: #fff; color: #1e293b; }
  .uvv-sig-table tr:last-child td { border-bottom: none; }
  .uvv-sig-table .signature-row td { height: 20px; }
  .uvv-instructor {
    margin-top: 8px; font-size: 12.5px; font-weight: 600; color: #0f172a;
    border-top: 1px solid #e2e8f0; padding-top: 6px;
  }
`;

/** PDF-Capture hat eigenes Außenpadding — Seiten-Padding daher 0, sonst Downscale/„Schrott“. */
const UVV_PDF_EXTRA_CSS = `
  ${UVV_DOC_EXTRA_CSS}
  .uvv-screen-only { display: none !important; }
  .uvv-a4-page {
    min-height: 0 !important; margin: 0 !important; padding: 0 !important;
    box-shadow: none !important; max-width: none !important; border-radius: 0 !important;
  }
  .uvv-page-label { display: none !important; }
`;

const isEmpty = (value: string) => !String(value || '').trim();

const todayIso = () => new Date().toISOString().split('T')[0];

const formatGermanDate = (iso: string) => {
    if (!iso) return '____________';
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return iso;
    return `${d}.${m}.${y}`;
};

const buildFormFromClient = (clientData: any): { form: FormData; locks: FieldLocks } => {
    const salonName = clientData?.salonname || clientData?.firmenname || '';
    const ownerName = clientData?.unternehmername || '';
    const street = clientData?.strasse || '';
    const zip = clientData?.plz || '';
    const city = clientData?.ort || '';

    return {
        form: {
            salonName,
            ownerName,
            street,
            zip,
            city,
            instructionDate: todayIso(),
            standortVerbandkasten: UVV_DEFAULT_VERBANDKASTEN,
            standortVerbandblock: UVV_DEFAULT_VERBANDBLOCK,
            standortFeuerloescher: UVV_DEFAULT_FEUERLOESCHER,
            unterweisungsTurnus: 'jahresunterweisung',
        },
        locks: {
            salonName: !isEmpty(salonName),
            ownerName: !isEmpty(ownerName),
            street: !isEmpty(street),
            zip: !isEmpty(zip),
            city: !isEmpty(city),
        },
    };
};

/** Migriert ältere Archiv-Payload-Feldnamen auf das aktuelle Schema */
const normalizeFormPayload = (raw: Record<string, any>, base: FormData): FormData => {
    const turnusRaw = raw.unterweisungsTurnus;
    const turnus: UvvUnterweisungsTurnus =
        turnusRaw === 'halbjahres_u18' ? 'halbjahres_u18' : 'jahresunterweisung';

    return {
        ...base,
        ...raw,
        standortVerbandkasten: String(
            raw.standortVerbandkasten ?? raw.verbandkastenStandort ?? base.standortVerbandkasten
        ),
        standortVerbandblock: String(
            raw.standortVerbandblock ?? base.standortVerbandblock
        ),
        standortFeuerloescher: String(
            raw.standortFeuerloescher ?? raw.feuerloescherStandort ?? base.standortFeuerloescher
        ),
        unterweisungsTurnus: turnus,
    };
};

const BgwUvvUnterweisungModal: React.FC<BgwUvvUnterweisungModalProps> = ({
    isOpen,
    onClose,
    initialData,
    onArchived,
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState<FormData>({
        salonName: '',
        ownerName: '',
        street: '',
        zip: '',
        city: '',
        instructionDate: todayIso(),
        standortVerbandkasten: UVV_DEFAULT_VERBANDKASTEN,
        standortVerbandblock: UVV_DEFAULT_VERBANDBLOCK,
        standortFeuerloescher: UVV_DEFAULT_FEUERLOESCHER,
        unterweisungsTurnus: 'jahresunterweisung',
    });
    const [fieldLocks, setFieldLocks] = useState<FieldLocks>({
        salonName: false,
        ownerName: false,
        street: false,
        zip: false,
        city: false,
    });
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const [archiveFeedback, setArchiveFeedback] = useState<'idle' | 'success' | 'error' | 'draft'>('idle');
    const [archiveDocId, setArchiveDocId] = useState<string | null>(null);

    const printContainerRef = useRef<HTMLDivElement>(null);
    const clientData = useAppStore((state: any) => state.clientData);

    // iOS: Hintergrund per position:fixed sperren — sonst blockiert der Body den Modal-Scroll / PDF-Flow
    useEffect(() => {
        if (!isOpen) return;
        const scrollY = window.scrollY;
        const prev = {
            overflow: document.body.style.overflow,
            position: document.body.style.position,
            top: document.body.style.top,
            width: document.body.style.width,
            htmlOverflow: document.documentElement.style.overflow,
        };
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        return () => {
            document.documentElement.style.overflow = prev.htmlOverflow;
            document.body.style.overflow = prev.overflow;
            document.body.style.position = prev.position;
            document.body.style.top = prev.top;
            document.body.style.width = prev.width;
            window.scrollTo(0, scrollY);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
            setInvalidFields(new Set());
            setDisclaimerAccepted(false);
            setIsSaving(false);
            setArchiveFeedback('idle');
            setArchiveDocId(null);
            return;
        }

        const { form, locks } = buildFormFromClient(clientData);
        setFormData(form);
        setFieldLocks(locks);
        setCurrentStep(1);
        setInvalidFields(new Set());
        setDisclaimerAccepted(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        setArchiveDocId(initialData?.id || null);

        if (initialData) {
            const payload = initialData.formData || initialData.payload?.formData;
            if (payload && typeof payload === 'object') {
                const normalized = normalizeFormPayload(payload, form);
                setFormData(normalized);
                setFieldLocks({
                    salonName: !isEmpty(String(normalized.salonName)),
                    ownerName: !isEmpty(String(normalized.ownerName)),
                    street: !isEmpty(String(normalized.street)),
                    zip: !isEmpty(String(normalized.zip)),
                    city: !isEmpty(String(normalized.city)),
                });
            }
            const step = Number(initialData.currentStep);
            if (Number.isFinite(step) && step >= 1 && step <= 2) {
                setCurrentStep(step);
            }
            if (initialData.confirmedDisclaimer) {
                setDisclaimerAccepted(true);
            }
        }
    }, [isOpen, clientData, initialData]);

    const validateStep1 = (): string[] => {
        const errors: string[] = [];
        if (isEmpty(formData.salonName)) errors.push('salonName');
        if (isEmpty(formData.ownerName)) errors.push('ownerName');
        if (isEmpty(formData.street)) errors.push('street');
        if (isEmpty(formData.zip)) errors.push('zip');
        if (isEmpty(formData.city)) errors.push('city');
        if (isEmpty(formData.instructionDate)) errors.push('instructionDate');
        if (isEmpty(formData.standortVerbandkasten)) errors.push('standortVerbandkasten');
        if (isEmpty(formData.standortVerbandblock)) errors.push('standortVerbandblock');
        if (isEmpty(formData.standortFeuerloescher)) errors.push('standortFeuerloescher');
        if (!formData.unterweisungsTurnus) errors.push('unterweisungsTurnus');
        return errors;
    };

    const isFieldInvalid = (field: string) => invalidFields.has(field);

    const getInputClass = (baseClass: string, field: string) =>
        isFieldInvalid(field) ? `${baseClass} ${ERROR_INPUT_RING}` : baseClass;

    const clearFieldError = (field: string) => {
        setInvalidFields((prev) => {
            if (!prev.has(field)) return prev;
            const next = new Set(prev);
            next.delete(field);
            return next;
        });
    };

    const updateField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        clearFieldError(key);
    };

    const renderFieldError = (field: string) =>
        isFieldInvalid(field) ? (
            <p className="text-[10px] text-red-400 mt-1">Dieses Feld ist erforderlich</p>
        ) : null;

    const handleWeiter = () => {
        const errors = validateStep1();
        if (errors.length > 0) {
            setInvalidFields(new Set(errors));
            return;
        }
        setInvalidFields(new Set());
        setCurrentStep(2);
    };

    const handleZurueck = () => {
        setInvalidFields(new Set());
        setCurrentStep(1);
    };

    const handleClose = () => {
        setCurrentStep(1);
        setInvalidFields(new Set());
        setDisclaimerAccepted(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        onClose();
    };

    const isActionLocked = !disclaimerAccepted;

    const getActionButtonClass = (baseClass: string) =>
        `${baseClass} ${isActionLocked ? 'opacity-50 cursor-not-allowed' : ''}`;

    const addressLine = [formData.street, [formData.zip, formData.city].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');

    const locationVars = {
        verbandkasten: formData.standortVerbandkasten || UVV_DEFAULT_VERBANDKASTEN,
        verbandblock: formData.standortVerbandblock || UVV_DEFAULT_VERBANDBLOCK,
        feuerloescher: formData.standortFeuerloescher || UVV_DEFAULT_FEUERLOESCHER,
    };

    const handlePrintIsolated = () => {
        if (isActionLocked) return;

        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);

        const printWindow = iframe.contentWindow;
        if (!printWindow) {
            document.body.removeChild(iframe);
            return;
        }

        const doc = printWindow.document;
        const printContent =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-uvv-unterweisung-print')?.innerHTML ||
            '';

        doc.open();
        doc.write(`
    <!DOCTYPE html>
    <html lang="de">
    <head>
      <meta charset="utf-8" />
      <title>BGW- &amp; UVV-Jahresunterweisung</title>
      <style>
        @page { size: A4; margin: 10mm; }
        body {
          font-family: "Segoe UI", Arial, Helvetica, sans-serif;
          color: #0f172a; background: #fff; margin: 0; padding: 0;
        }
        ${UVV_PDF_EXTRA_CSS}
        .uvv-a4-page {
          box-shadow: none !important; max-width: none !important;
          min-height: auto !important; margin: 0 !important; padding: 0 !important;
        }
        .uvv-screen-only, .uvv-page-label { display: none !important; }
        /* Exakt 2 Seiten: Umbruch NUR vor Seite 2, Abschnitte nicht mittendrin splitten */
        .print-break, .pepapers-print-break {
          page-break-before: always !important;
          break-before: page !important;
        }
        .uvv-section, .uvv-sig-wrap {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
      </style>
    </head>
    <body>
      ${printContent}
    </body>
    </html>
  `);
        doc.close();

        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            setTimeout(() => {
                if (iframe.parentNode) {
                    document.body.removeChild(iframe);
                }
            }, 1000);
        }, 500);
    };

    const handleDownloadPdf = async () => {
        if (isActionLocked || isPdfDownloading) return;
        const html =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-uvv-unterweisung-print')?.innerHTML ||
            '';
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            const dateIso = formData.instructionDate || new Date().toISOString().slice(0, 10);
            await downloadHtmlAsPdf({
                html,
                filename: `BGW_UVV_Jahresunterweisung_${dateIso}.pdf`,
                extraCss: UVV_PDF_EXTRA_CSS,
                splitPrintBreaks: true,
                // Klassen behalten + jede Sektion exakt 1 A4 (kein Canvas-Schnitt/Weißloch)
                scrubMode: 'minimal',
                fitSectionsToPage: true,
                letterheadOnBreaks: false,
            });
        } catch (err: any) {
            console.error('PEPapers UVV-Unterweisung PDF-Download fehlgeschlagen:', err);
            window.alert(
                'PDF konnte nicht heruntergeladen werden: ' +
                    (String(err?.message || '').match(/setEnd|Range|offset/i)
                        ? 'PDF-Erstellung ist fehlgeschlagen. Bitte Seite neu laden und erneut versuchen.'
                        : err?.message || 'Unbekannter Fehler')
            );
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handleArchive = async (asDraft = false) => {
        if (isSaving) return;
        if (!asDraft && isActionLocked) return;

        setIsSaving(true);
        setArchiveFeedback('idle');

        const htmlContent =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-uvv-unterweisung-print')?.innerHTML ||
            '';

        try {
            const id = await savePePapersArchiveDoc({
                id: archiveDocId,
                documentType: 'bgw-uvv-jahresunterweisung',
                status: asDraft ? 'entwurf' : 'final',
                salonName: formData.salonName || 'Unbekannter Salon',
                confirmedDisclaimer: asDraft ? disclaimerAccepted : true,
                currentStep,
                payload: {
                    formData,
                    documentId: UVV_DOC_ID,
                    generatedHtml: htmlContent,
                },
            });
            setArchiveDocId(id);
            setArchiveFeedback(asDraft ? 'draft' : 'success');
            onArchived?.();
            if (asDraft) {
                setTimeout(() => {
                    onClose();
                }, 600);
            } else {
                setTimeout(() => setArchiveFeedback('idle'), 3000);
            }
        } catch (error) {
            console.error('PEPapers UVV-Unterweisung Archivierung fehlgeschlagen:', error);
            setArchiveFeedback('error');
        } finally {
            setIsSaving(false);
        }
    };

    const renderLockedOrEditable = (
        field: keyof FieldLocks,
        label: string,
        value: string,
        onChange: (v: string) => void
    ) => {
        const isLocked = fieldLocks[field];
        return (
            <div>
                <label className={LABEL_CLASS}>{label}</label>
                <input
                    type="text"
                    value={value}
                    readOnly={isLocked}
                    onChange={(e) => !isLocked && onChange(e.target.value)}
                    className={getInputClass(isLocked ? READONLY_INPUT_CLASS : INPUT_CLASS, field)}
                />
                {renderFieldError(field)}
            </div>
        );
    };

    const renderSectionTitle = (title: string) => {
        // Nur echte Prüfpunkt-Titel bekommen ein Kästchen; Prefix "[ ]" wird entfernt.
        const label = String(title || '')
            .replace(/^\[\s*[xX ]?\s*\]\s*/, '')
            .trim();
        if (!label) return null;
        return (
            <h4 className="uvv-sec-title">
                <span className="uvv-tickbox" aria-hidden="true" />
                <span className="uvv-tick-label">{label}</span>
            </h4>
        );
    };

    const renderModuleBlock = (mod: UvvModule) => (
        <div key={mod.id} className="uvv-module">
            <h3 className="uvv-mod-title">
                MODUL {mod.number}: {mod.title}
            </h3>
            {mod.sections.map((section) => {
                const bullets = (section.bullets || []).filter(
                    (b) => String(b || '').replace(/^\[\s*\]\s*/, '').trim().length > 0
                );
                return (
                    <div key={`${mod.id}-${section.title}`} className="uvv-section">
                        {renderSectionTitle(section.title)}
                        {bullets.length > 0 ? (
                            <ul>
                                {bullets.map((bullet, bi) => (
                                    <li key={bi}>{resolveUvvBullet(bullet, locationVars)}</li>
                                ))}
                            </ul>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );

    const renderSignatureTable = () => (
        <div className="uvv-sig-wrap">
            <table className="uvv-sig-table">
                <thead>
                    <tr>
                        <th style={{ width: '7%' }}>Nr.</th>
                        <th style={{ width: '28%' }}>Name des Mitarbeiters</th>
                        <th style={{ width: '14%' }}>Azubi / U18</th>
                        <th style={{ width: '16%' }}>Unterwiesen am</th>
                        <th style={{ width: '35%' }}>Unterschrift</th>
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: UVV_SIGNATURE_ROW_COUNT }, (_, index) => (
                        <tr key={index} className="signature-row">
                            <td>{index + 1}</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderPageHeader = () => (
        <>
            <table className="uvv-header">
                <tbody>
                    <tr>
                        <td className="uvv-header-logo">
                            <img src={PE_LOGO_SRC} alt="PreisExpert" className="uvv-logo" />
                        </td>
                        <td>
                            <p className="uvv-brand-kicker">PreisExpert</p>
                            <h1 className="uvv-doc-title">
                                <span className="uvv-title-pe">PE</span>
                                <span className="uvv-title-rest">Papers</span>
                                <span className="uvv-title-sub">BGW- &amp; UVV-Unterweisung</span>
                            </h1>
                            <p className="uvv-doc-sub">{UVV_DOC_SUBTITLE}</p>
                        </td>
                        <td className="uvv-header-meta">
                            <p>
                                Dokument: <strong>{UVV_DOC_ID}</strong>
                            </p>
                            <p>
                                {'Datum'}: <strong>{formatGermanDate(formData.instructionDate)}</strong>
                            </p>
                            <p>
                                Betrieb: <strong>{formData.salonName || '—'}</strong>
                            </p>
                        </td>
                    </tr>
                </tbody>
            </table>

            <p className="uvv-lead">
                {UVV_INTERVAL_HINT}
                <br />
                <strong>
                    Abhakliste für das Teamgespräch: Jeden Punkt mit dem Kästchen □ im Gespräch
                    abhaken (z.&nbsp;B. mit Azubis / Mitarbeitern).
                </strong>
            </p>

            <p className="uvv-stammdaten">
                <strong>Inhaber:</strong> {formData.ownerName || '____________'}
                <br />
                <strong>Adresse:</strong> {addressLine || '____________'}
                {' · '}
                <strong>{'Datum'}:</strong> {formatGermanDate(formData.instructionDate)}
                <br />
                {formatUvvTurnusLine(formData.unterweisungsTurnus)}
                <br />
                <strong>Verbandkasten:</strong> {locationVars.verbandkasten}
                {' · '}
                <strong>Verbandblock:</strong> {locationVars.verbandblock}
                {' · '}
                <strong>Feuerlöscher:</strong> {locationVars.feuerloescher}
            </p>
        </>
    );

    /**
     * EXAKT 2 Seiten — Umbruch nur einmal zwischen Seite 1 und 2.
     * Innerhalb der Seiten: .uvv-section bleibt zusammen (kein Schnitt mitten im Satz).
     * Seite 1: Kopf + Modul 1–3 | Seite 2: Modul 4–5 + Unterschriften
     */
    const renderDocument = () => (
        <div
            ref={printContainerRef}
            id="pepapers-uvv-unterweisung-print"
            className="w-full h-auto overflow-visible"
        >
            <style>{UVV_DOC_EXTRA_CSS}</style>

            <div className={A4_PAGE_CLASS}>
                <p className="uvv-screen-only uvv-page-label">Seite 1 von 2</p>
                {renderPageHeader()}
                {UVV_MODULES_PAGE1.map(renderModuleBlock)}
            </div>

            <div className={`print-break pepapers-print-break ${A4_PAGE_CLASS}`}>
                <p className="uvv-screen-only uvv-page-label">Seite 2 von 2</p>
                {UVV_MODULES_PAGE2.map(renderModuleBlock)}
                <div className="uvv-module">
                    <h3 className="uvv-mod-title">MODUL 6: TEILNAHME- UND UNTERWEISUNGSNACHWEIS</h3>
                    <p className="uvv-confirm">{UVV_CONFIRMATION_TEXT}</p>
                    <p className="uvv-confirm" style={{ marginBottom: 4 }}>
                        <strong>Betrieb:</strong> {formData.salonName || '____________'} ·{' '}
                        <strong>Datum:</strong> {formatGermanDate(formData.instructionDate)}
                    </p>
                    {renderSignatureTable()}
                    <p className="uvv-instructor">{UVV_INSTRUCTOR_SIGNATURE_LABEL}</p>
                </div>
            </div>
        </div>
    );

    if (!isOpen) return null;

    const modalWidthClass = currentStep === 2 ? 'max-w-5xl' : 'max-w-lg';

    return createPortal(
        <>
            <div
                className="fixed inset-0 bg-stone-900/70 dark:bg-black/90 z-[100000] backdrop-blur-md print:hidden"
                onClick={handleClose}
                aria-hidden="true"
            />
            <div className="fixed inset-x-0 top-[8.5rem] md:top-36 bottom-0 z-[100001] flex items-start justify-center overflow-hidden p-4 pb-8 font-sans print:inset-0 print:top-0 print:overflow-visible print:p-0">
                <div
                    className={`bg-pe-card dark:bg-[#111] rounded-2xl shadow-[0_0_40px_rgba(230,194,153,0.1)] border border-[#E6C299]/30 w-full ${modalWidthClass} max-h-[calc(100vh-9.5rem)] md:max-h-[calc(100vh-10rem)] flex min-h-0 flex-col justify-between overflow-hidden relative transition-all duration-200 my-2 print:shadow-none print:border-0 print:max-w-none print:max-h-none print:rounded-none print:my-0 print:bg-white`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute top-4 right-4 text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white bg-pe-elevated dark:bg-[#1a1a1a] hover:bg-pe-hover dark:hover:bg-[#222] rounded-lg p-2 transition-colors border border-pe-border-strong dark:border-[#333] z-[60] print:hidden"
                        aria-label="Schließen"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="bg-pe-elevated dark:bg-[#111622] z-50 shrink-0 print:hidden border-b border-pe-border dark:border-[#222]">
                        <div className="px-6 pt-10 pb-4">
                            <h3 className="text-lg font-bold text-pe-accent-text dark:text-[#E6C299] text-center uppercase tracking-widest pr-8 leading-snug">
                                BGW- &amp; UVV-Jahresunterweisung
                            </h3>
                            <p className="text-center text-xs text-pe-muted dark:text-slate-400 uppercase tracking-widest mt-2">
                                Schritt {currentStep} von 2: {STEP_LABELS[currentStep]}
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 custom-scrollbar print:p-0 print:overflow-visible">
                        {currentStep === 1 && (
                            <div className="space-y-4">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                    Betriebs- &amp; Unterweisungsdaten
                                </p>
                                <p className="text-xs text-stone-500 dark:text-slate-500 leading-relaxed mb-4">
                                    Salon- und Inhaberdaten werden aus dem Profil übernommen. Turnus und Standorte
                                    (Verbandkasten, Meldeblock, Feuerlöscher) bitte prüfen und anpassen.
                                </p>
                                <div className="space-y-4">
                                    {renderLockedOrEditable('salonName', 'Firma / Salonname', formData.salonName, (v) =>
                                        updateField('salonName', v)
                                    )}
                                    {renderLockedOrEditable(
                                        'ownerName',
                                        'Inhaber / Unterweisender',
                                        formData.ownerName,
                                        (v) => updateField('ownerName', v)
                                    )}
                                    {renderLockedOrEditable('street', 'Straße & Hausnummer', formData.street, (v) =>
                                        updateField('street', v)
                                    )}
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        {renderLockedOrEditable('zip', 'PLZ', formData.zip, (v) => updateField('zip', v))}
                                        <div className="sm:col-span-2">
                                            {renderLockedOrEditable('city', 'Ort', formData.city, (v) =>
                                                updateField('city', v)
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>Unterweisungsdatum</label>
                                        <input
                                            type="date"
                                            value={formData.instructionDate}
                                            onChange={(e) => updateField('instructionDate', e.target.value)}
                                            className={getInputClass(INPUT_CLASS, 'instructionDate')}
                                        />
                                        {renderFieldError('instructionDate')}
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>Unterweisungs-Turnus</label>
                                        <div
                                            className={`space-y-2 rounded-lg border px-3 py-3 ${
                                                isFieldInvalid('unterweisungsTurnus')
                                                    ? 'border-red-500'
                                                    : 'border-pe-border dark:border-slate-700'
                                            }`}
                                        >
                                            <label className="flex items-start gap-2 cursor-pointer text-sm text-pe-text dark:text-slate-200">
                                                <input
                                                    type="radio"
                                                    name="unterweisungsTurnus"
                                                    checked={formData.unterweisungsTurnus === 'jahresunterweisung'}
                                                    onChange={() =>
                                                        updateField('unterweisungsTurnus', 'jahresunterweisung')
                                                    }
                                                    className="mt-1"
                                                />
                                                <span>
                                                    Jährliche Unterweisung (Erwachsene Mitarbeiter gem. § 12 ArbSchG)
                                                </span>
                                            </label>
                                            <label className="flex items-start gap-2 cursor-pointer text-sm text-pe-text dark:text-slate-200">
                                                <input
                                                    type="radio"
                                                    name="unterweisungsTurnus"
                                                    checked={formData.unterweisungsTurnus === 'halbjahres_u18'}
                                                    onChange={() =>
                                                        updateField('unterweisungsTurnus', 'halbjahres_u18')
                                                    }
                                                    className="mt-1"
                                                />
                                                <span>
                                                    Halbjährliche Unterweisung (Auszubildende &amp; Jugendliche unter 18
                                                    Jahren gem. § 29 JArbSchG)
                                                </span>
                                            </label>
                                        </div>
                                        {renderFieldError('unterweisungsTurnus')}
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>Standort Verbandkasten</label>
                                        <input
                                            type="text"
                                            value={formData.standortVerbandkasten}
                                            onChange={(e) => updateField('standortVerbandkasten', e.target.value)}
                                            placeholder="z. B. Mixecke"
                                            className={getInputClass(INPUT_CLASS, 'standortVerbandkasten')}
                                        />
                                        {renderFieldError('standortVerbandkasten')}
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>Standort Verbandblock / Meldeblock</label>
                                        <input
                                            type="text"
                                            value={formData.standortVerbandblock}
                                            onChange={(e) => updateField('standortVerbandblock', e.target.value)}
                                            placeholder="z. B. Büro / verschlossener Schrank"
                                            className={getInputClass(INPUT_CLASS, 'standortVerbandblock')}
                                        />
                                        {renderFieldError('standortVerbandblock')}
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>Standort Feuerlöscher</label>
                                        <input
                                            type="text"
                                            value={formData.standortFeuerloescher}
                                            onChange={(e) => updateField('standortFeuerloescher', e.target.value)}
                                            placeholder="z. B. Eingang / Notausgang"
                                            className={getInputClass(INPUT_CLASS, 'standortFeuerloescher')}
                                        />
                                        {renderFieldError('standortFeuerloescher')}
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="w-full overflow-visible pr-1 pb-8">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-pe-muted dark:text-slate-500 mb-3 print:hidden">
                                    Vorschau — 2 A4-Seiten
                                </p>
                                {renderDocument()}
                            </div>
                        )}
                    </div>

                    {currentStep === 2 && (
                        <div className="px-4 sm:px-6 py-3 shrink-0 print:hidden border-t border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#111] relative z-10">
                            <div
                                className={`rounded-xl border-2 transition-all duration-200 ${
                                    disclaimerAccepted
                                        ? 'border-emerald-500/40 bg-emerald-500/5 px-3 py-2.5'
                                        : 'border-amber-500 bg-amber-500/15 dark:bg-amber-500/10 shadow-[0_0_24px_rgba(245,158,11,0.25)] p-3 sm:p-4'
                                }`}
                            >
                                {!disclaimerAccepted && (
                                    <>
                                        <p className="mb-1 text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-amber-800 dark:text-amber-300">
                                            Pflicht · Rechtlicher Hinweis bestätigen
                                        </p>
                                        <p className="mb-2 text-[11px] sm:text-xs font-semibold text-amber-950 dark:text-amber-100/95 leading-snug">
                                            Ohne diese Bestätigung bleiben PDF-Download, Druck und Archiv-Speichern
                                            gesperrt.
                                        </p>
                                    </>
                                )}
                                <label className="flex items-start gap-3 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={disclaimerAccepted}
                                        onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                                        className="mt-0.5 w-7 h-7 sm:w-6 sm:h-6 shrink-0 cursor-pointer appearance-none bg-pe-input dark:bg-black border-2 border-amber-600 dark:border-amber-400 rounded-md checked:bg-[#E6C299] checked:border-[#E6C299] relative transition-colors after:content-['✔'] after:absolute after:text-black after:text-[14px] after:font-black after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:opacity-0 checked:after:opacity-100"
                                    />
                                    <span
                                        className={
                                            disclaimerAccepted
                                                ? 'text-sm font-semibold text-emerald-800 dark:text-emerald-300 leading-snug'
                                                : 'text-sm text-pe-text dark:text-slate-200 leading-snug'
                                        }
                                    >
                                        {disclaimerAccepted
                                            ? 'Rechtlicher Hinweis bestätigt'
                                            : 'Ich habe die Daten geprüft und bestätige ausdrücklich, dass ich den rechtlichen Hinweis verstanden habe. Mir ist bekannt, dass es sich hierbei um ein automatisiertes Standard-Muster und keine individuelle Rechtsberatung handelt. Die Verwendung erfolgt auf eigene Gefahr und Verantwortung.'}
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}

                    {currentStep === 2 && (
                        <div className="px-6 pt-3 print:hidden">
                            <PePapersStorageHint
                                hint={PE_PAPERS_STORAGE_HINTS['bgw-uvv-unterweisung']}
                                variant="modal"
                            />
                        </div>
                    )}

                    <div className="px-6 py-4 border-t border-pe-border dark:border-[#222] shrink-0 print:hidden">
                        <div className="flex flex-wrap gap-3">
                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={handleZurueck}
                                    className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors"
                                >
                                    Zurück
                                </button>
                            )}

                            {currentStep === 1 ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {isSaving && archiveFeedback !== 'draft' ? 'Speichert...' : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleWeiter}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white text-black transition-colors"
                                    >
                                        Dokument erstellen
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving}
                                        className="flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {archiveFeedback === 'draft' ? 'Gespeichert! ✓' : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(false)}
                                        disabled={isActionLocked || isSaving}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-pe-card dark:bg-[#15171e] hover:bg-pe-hover dark:hover:bg-[#1a1d24] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                        )}
                                    >
                                        {isSaving && archiveFeedback !== 'draft'
                                            ? 'Speichert...'
                                            : archiveFeedback === 'success'
                                              ? 'Gespeichert! ✓'
                                              : 'Im Archiv speichern'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleDownloadPdf()}
                                        disabled={isActionLocked || isPdfDownloading}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-gradient-to-b from-[#E6C299] to-[#b48a56] hover:from-[#f0d3b3] hover:to-[#cfa674] text-black transition-colors'
                                        )}
                                    >
                                        {isPdfDownloading ? 'PDF wird erstellt...' : 'Als PDF herunterladen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handlePrintIsolated}
                                        disabled={isActionLocked}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                        )}
                                    >
                                        Ausdrucken
                                    </button>
                                </>
                            )}
                        </div>
                        {currentStep === 2 && archiveFeedback === 'error' && (
                            <p className="text-[10px] text-red-400 mt-2 text-center">
                                Archivierung fehlgeschlagen. Bitte erneut versuchen.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default BgwUvvUnterweisungModal;
